import { useScopedRequests } from "../../../../shared/useScopedRequests";
import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from "react";
import services from "../services/indexService";
import useToast from "../hooks/useToast";
import AlertPopup from "../components/index/AlertPopup";
import { policyNumberCheck } from "../../utils/regexCheck";
import { appInsights, track, trackEvent, trackError } from "../../telemetry/telemetryService";

export const taskPaneInitailValue = {
  // Index
  docDescription: "",
  documentClass: "",
  documentType: "",
  receivedDate: "",
  chkQuoteSentToProducer: false,
  useFileName: false,
  docNotes: "",
  // task
  taskChecked: false,
  taskSubject: "",
  sameAsDocDesc: false,
  taskQueue: "",
  taskType: "",
  taskNote: "",
  taskDueDate: "",
  taskStartDate: "",
  taskPriority: "",
  checklistItem: "",
};
const TaskPaneContext = createContext({
  selectedGridItem: [],
  selectedItemDetailFromGrid: null,
  setSelectedGridItem: (_selectedItems) => {},
  queues: [],
  setQueues: (_queues = []) => {},
  userGroups: [],
  setUserGroups: (_values = []) => {},
  user: [],
  setUsers: (_values = []) => {},
  taskTypes: [],
  setTaskTypes: (_values = []) => {},
  taskPaneState: {
    searchText: null,
    compId: null,
    comLocId: null,
    criteria: null,
    insuredId: null,
    userId: null,
    userGroupId: null,
    taskTypeId: null,
  },
  accessToken: null,
});

export const TaskPaneProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [taskPaneState, setTaskPaneState] = useState({
    searchText: null,
    compId: null,
    comLocId: null,
    criteria: null,
    insuredId: null,
    userId: null,
    userGroupId: null,
  });
  //
  const [username, setUsername] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const isDialog = new URLSearchParams(window.location.search).get("dialog") === "true";
  //
  const { notify, Toaster } = useToast();
  const [profileDetails, setProfileDetails] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [policiesDetailsForInsured, setPoliciesDetailsForInsured] = useState([]);
  const [selectedGridItem, setSelectedGridItem] = useState([]);
  const [priorityList, setPriorityList] = useState([]);
  const [documentType, setDocumentType] = useState([]);
  const [documentClass, setDocumentClass] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [queues, setQueues] = useState([]);
  const [user, setUsers] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [checkList, setCheckList] = useState([]);
  const [open, setOpen] = useState(false);
  const selectedPolicyId = [...selectedGridItem][0] || "";
  const baseScope = [taskPaneState.userId, taskPaneState.compId, taskPaneState.comLocId];
  const requests = useScopedRequests({
    location: baseScope,
    types: baseScope,
    groups: [taskPaneState.userId],
    classes: [...baseScope, taskPaneState.criteria],
    policies: [...baseScope, taskPaneState.criteria, taskPaneState.insuredId],
    details: [...baseScope, taskPaneState.criteria, selectedPolicyId, taskPaneState.documentType],
    checklist: [...baseScope, selectedPolicyId],
    queues: [...baseScope, taskPaneState.userGroupId],
    users: [...baseScope, taskPaneState.userGroupId],
    tasks: [...baseScope, taskPaneState.userGroupId],
    locations: [taskPaneState.userId, username],
    emailLocation: baseScope,
  });
  const metadataLoading = requests.pending;

  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  // Bootstrap loading flags so dropdowns/buttons can render explicit "Loading..." states
  // instead of sitting silently disabled while the auth -> locations -> criteria chain runs.
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const retryLocations = () => getLocationData();
  const [criteriaLoading, setCriteriaLoading] = useState(false);
  // Doc Type + Doc Class load in parallel on comLocId change; a ref-counter keeps the shared
  // flag true until BOTH settle (a plain boolean would flip false when the first one lands).
  const [formListsLoading, setFormListsLoading] = useState(false);
  const formListsPending = useRef(0);
  const beginFormLists = () => {
    formListsPending.current += 1;
    setFormListsLoading(true);
  };
  const endFormLists = () => {
    formListsPending.current = Math.max(0, formListsPending.current - 1);
    if (formListsPending.current === 0) setFormListsLoading(false);
  };
  // Office host detected at startup: an Office.HostType when hosted, null in a plain browser.
  // undefined = not yet resolved. Recorded for telemetry; the location bootstrap is now
  // host-AGNOSTIC (fires on Outlook AND browser) -- see the unified bootstrap effect below.
  const [officeHost, setOfficeHost] = useState(undefined);
  // SINGLE one-shot guard shared by every path that can load locations (the generic
  // authenticated bootstrap below, the Outlook mail-subject path in setSubject, and the
  // email policy/submission branch in getLocationSpecificInfoEmail). Whichever fires first
  // sets it; the others no-op -> getLocationData runs EXACTLY ONCE per session.
  const locationBootstrapped = useRef(false);
  const getPoliciesDetailsForInsuredData = async (
    InsuredID,
    SubmissionStatusID,
    LoggedInCompLocID,
    ViewAllCompLocs,
  ) => {
    const operation = requests.start("policies");
    try {
      setPoliciesDetailsForInsured([]);
      setSelectedGridItem([]);
      setTaskPaneState((prev) => ({ ...prev }));
      try {
        const result = await services.getPoliciesDetailsForInsured(
          InsuredID,
          SubmissionStatusID,
          LoggedInCompLocID,
          ViewAllCompLocs,
          taskPaneState?.userId,
        );
        if (!requests.current(operation)) return;
        if (!!result && result?.length) {
          setSelectedGridItem(
            result?.length
              ? new Set([taskPaneState?.isEmailSub ? taskPaneState?.policyId : result[0]?.policyID])
              : [],
          );
          setPoliciesDetailsForInsured(result);
          setTaskPaneState((prev) => ({ ...prev, isEmailSub: false }));
          return result;
        } else {
          setTitle("Search result");
          setText("No policy details found");
          // setOpen(true);
        }
      } catch (e) {
        if (!requests.current(operation)) return;
        if (e?.response?.status === 403) {
          setTitle(e?.response?.data?.error === "AccessDenied" ? "Access Denied" : "Search Failed");
          setText(e?.response?.data?.message);
          // setOpen(true);
        }
        setTaskPaneState((prev) => ({ ...prev }));
        setPoliciesDetailsForInsured([]);
        setSelectedGridItem([]);
      }
    } finally {
      requests.finish(operation);
    }
  };

  useEffect(() => {
    if (taskPaneState.comLocId) getPriorityList();
  }, [taskPaneState.comLocId]);

  const getPriorityList = async () => {
    try {
      const result = await services.getPriorityList();
      setPriorityList(result);
    } catch (e) {
      setPriorityList([]);
      console.log(e);
    }
  };

  const filterTypeByCriteriaType = useMemo(
    () =>
      taskPaneState?.criteria === "Insured" ||
      taskPaneState?.criteria === "Policy #" ||
      taskPaneState?.criteria === "Submission #"
        ? "Type Insured/Policy"
        : taskPaneState?.criteria === "Producer #" || taskPaneState?.criteria === "Producer Name"
          ? "Type Producer"
          : "Type claim",
    [taskPaneState?.criteria],
  );

  useEffect(() => {
    if (!taskPaneState.comLocId) {
      setPoliciesDetailsForInsured([]);
      setSelectedGridItem([]);
      setDocumentType([]);
      setDocumentClass([]);
      setCheckList([]);
    }
  }, [taskPaneState.comLocId]);
  const selectedItemDetailFromGrid = useMemo(() => {
    if (filterTypeByCriteriaType === "Type Insured/Policy" && policiesDetailsForInsured) {
      const selectedPolicy = policiesDetailsForInsured?.find(
        (item) => item.policyID === [...selectedGridItem][0],
      );
      setTaskPaneState((prev) => ({
        ...prev,
        // receivedDate: formatToYyyyMmDd(selectedPolicy?.receiveDate),
        chkQuoteSentToProducer: !!selectedPolicy?.chkQuoteSentToProducer ?? false,
        unlockchkQuoteSentToProducer: !!selectedPolicy?.unlockchkQuoteSentToProducer ?? false,
        policychkQuoteSentToProducer: !!selectedPolicy?.policychkQuoteSentToProducer ?? false,
        //docDescription: selectedPolicy?.docDescription?.length ? selectedPolicy.docDescription.substring(0, 60) : "", documentType: selectedPolicy?.documentType ?? "",
        //documentType: selectedPolicy?.documentType ?? "",
        // useFileName: !!selectedPolicy?.useFileName ?? false,
        //documentClass: selectedPolicy?.documentClass ?? "",
        // emailSubject: selectedPolicy?.emailSubject ?? "",
        docNotes: selectedPolicy?.docNotes?.length ? selectedPolicy?.docNotes.substring(0, 255) : "",

        useChecklistItem: false,
        docDescription: prev?.useChecklistItem ? "" : prev?.docDescription,
        sameAsDocDesc: !!prev?.useChecklistItem && !!prev?.sameAsDocDesc ? false : !!prev?.sameAsDocDesc,
        taskSubject: !!prev?.useChecklistItem && !!prev?.sameAsDocDesc ? "" : prev?.taskSubject,
      }));
      return selectedPolicy;
    } else {
      const selectedProducer = policiesDetailsForInsured?.find(
        (item) => item.producerId === [...selectedGridItem][0],
      );
      return {
        ProducerID: selectedProducer?.producerId,
        policyNumber: "",
        lob: "",
      };
    }
  }, [selectedGridItem, policiesDetailsForInsured, filterTypeByCriteriaType]);

  useEffect(() => {
    if (
      selectedItemDetailFromGrid?.policyID &&
      taskPaneState.comLocId &&
      taskPaneState.userId &&
      taskPaneState.documentType
    ) {
      getIndexFormDetails(
        taskPaneState?.documentType,
        taskPaneState.comLocId,
        !!sessionStorage.getItem("actionType"),
        selectedItemDetailFromGrid?.policyID,
        null,
        taskPaneState.userId,
        taskPaneState.emailSubject,
      );
    } else {
      if (!selectedItemDetailFromGrid?.ProducerID && !selectedItemDetailFromGrid?.policyID) {
        setTaskPaneState((prev) => ({
          ...prev,
          ...taskPaneInitailValue,
          userGroupId: prev?.profileUserGroupId,
          userGroup: prev?.userId,
        }));
      }
    }
  }, [taskPaneState?.documentType, selectedItemDetailFromGrid?.policyID]);

  const getIndexFormDetails = async (
    imgKeywordId,
    CompLocID,
    EmailIndex,
    policyID,
    policyDetailId,
    userId,
    txtSubject,
  ) => {
    const operation = requests.start("details");
    setTaskPaneState((prev) => ({ ...prev, documentClass: "" }));
    try {
      //notify("progress");
      const result = await services.GetIdsByImageKeywordSelect(
        imgKeywordId,
        CompLocID,
        true,
        policyID,
        policyDetailId,
        userId,
        txtSubject,
      );
      if (!requests.current(operation)) return;
      setTaskPaneState((prev) => ({
        ...prev,
        docDescription: result?.docDescription?.length ? result?.docDescription.substring(0, 60) : "",
        documentClass: result?.documentClassID ?? "",
        // documentType: result?.imgKeywordID ?? "",
        // receivedDate: formatToYyyyMmDd(result?.receiveDate),
        // chkQuoteSentToProducer: result?.chkQuoteSentToProducer ?? false,
        useFileName: result?.docDescription?.length ? false : prev?.useFileName,
        useChecklistItem: result?.docDescription?.length ? false : prev?.useChecklistItem,
        // emailSubject: result?.subject ? result?.subject: taskPaneState?.emailSubject,
        // docNotes: result?.docNotes ?? "",
        // task
        taskChecked: result?.chkTask,
        taskSubject: result?.txtSubject?.length ? result?.txtSubject.substring(0, 60) : "",
        sameAsDocDesc:
          result?.docDescription?.length || result?.txtSubject?.length ? false : prev?.sameAsDocDesc,
        userGroupId: result?.userGroupID ? result?.userGroupID : taskPaneState?.profileUserGroupId,
        userGroup: result?.assignedId ? result?.assignedId : taskPaneState?.userId,
        taskQueue: result?.imgCommonQueueID,
        taskType: result?.taskTypeID,
        // taskNote: result?.docNotes?? "",
        taskDueDate: result?.dueDateOnly,
        taskStartDate: result?.startDateOnly,
        taskPriority: result?.priorityID,
      }));
    } catch (e) {
      if (!requests.current(operation)) return;
      //notify("error", "Failed to fetch GetIdsByImageKeywordSelect data");
      console.log(e);
    } finally {
      requests.finish(operation);
    }
  };
  const getDocumentType = async (comLocId) => {
    const operation = requests.start("types");
    try {
      beginFormLists();
      try {
        const result = await services.getDocumentType(comLocId);
        if (!requests.current(operation)) return;
        setDocumentType(result);
      } catch (e) {
        if (!requests.current(operation)) return;
        setDocumentType([]);
        console.log(e);
      } finally {
        endFormLists();
      }
    } finally {
      requests.finish(operation);
    }
  };

  const getDocumentClass = async (comLocId, criteria) => {
    const operation = requests.start("classes");
    try {
      beginFormLists();
      try {
        const result = await services.getDocumentClass(criteria, comLocId, false);
        if (!requests.current(operation)) return;
        setDocumentClass(result);
      } catch (e) {
        if (!requests.current(operation)) return;
        setDocumentClass([]);
        console.log(e);
      } finally {
        endFormLists();
      }
    } finally {
      requests.finish(operation);
    }
  };

  const getUserGroupsForPerson = async (userId) => {
    const operation = requests.start("groups");
    try {
      const result = await services.getUserGroupsForPerson(userId);
      if (!requests.current(operation)) return;
      setUserGroups(result);
      setTaskPaneState((prev) => ({ ...prev }));
    } catch (e) {
      if (!requests.current(operation)) return;
      console.log(e);
      setUserGroups([]);
    } finally {
      requests.finish(operation);
    }
  };
  const getQueuesForUserGroup = async (userGroupId) => {
    const operation = requests.start("queues");
    try {
      const result = await services.getQueuesForUserGroup(userGroupId);
      if (!requests.current(operation)) return;
      setQueues(result);
      setTaskPaneState((prev) => ({ ...prev }));
    } catch (e) {
      if (!requests.current(operation)) return;
      console.log(e);
      setQueues([]);
    } finally {
      requests.finish(operation);
    }
  };

  const getPersonUserGroupXRef = async (userGroupId, activeOnly, activeAndExpiredWithOpenTask) => {
    const operation = requests.start("users");
    try {
      const result = await services.getPersonUserGroupXRef(
        userGroupId,
        activeOnly,
        activeAndExpiredWithOpenTask,
      );
      if (!requests.current(operation)) return;
      setUsers(result);
      setTaskPaneState((prev) => ({ ...prev }));
    } catch (e) {
      if (!requests.current(operation)) return;
      setUsers([]);
      console.log(e);
    } finally {
      requests.finish(operation);
    }
  };
  const getCheckList = async (PolicyId, Completed, CompLocID, IncludeNone) => {
    const operation = requests.start("checklist");
    try {
      setTaskPaneState((prev) => ({ ...prev, checklistItem: "" }));
      const result = await services.getPolicyChecklistForPolicy(PolicyId, Completed, CompLocID, IncludeNone);
      if (!requests.current(operation)) return;
      setCheckList(result);
    } catch (e) {
      if (!requests.current(operation)) return;
      setCheckList([]);
      setTaskPaneState((prev) => ({ ...prev, checklistItem: "" }));
    } finally {
      requests.finish(operation);
    }
  };
  const getTaskTypes = async (userGroupId) => {
    const operation = requests.start("tasks");
    try {
      const result = await services.getTaskTypes(0, userGroupId);
      if (!requests.current(operation)) return;
      setTaskTypes(result);
    } catch (e) {
      if (!requests.current(operation)) return;
      setTaskTypes([]);
    } finally {
      requests.finish(operation);
    }
  };
  useEffect(() => {
    if (taskPaneState.comLocId) {
      getDocumentType(taskPaneState.comLocId);
    }
  }, [taskPaneState.comLocId]);

  useEffect(() => {
    if (taskPaneState.comLocId && taskPaneState.criteria) {
      getDocumentClass(taskPaneState.comLocId, taskPaneState.criteria);
    }
  }, [taskPaneState.comLocId, taskPaneState.criteria]);
  useEffect(() => {
    if (
      taskPaneState?.insuredId &&
      taskPaneState.comLocId &&
      (taskPaneState?.criteria === "Policy #" ||
        taskPaneState?.criteria === "Insured" ||
        taskPaneState?.criteria === "Submission #")
    ) {
      if (!taskPaneState?.isSearch)
        getPoliciesDetailsForInsuredData(taskPaneState?.insuredId, -1, taskPaneState.comLocId, false);
      else setTaskPaneState((prev) => ({ ...prev, isSearch: false }));
    }
  }, [taskPaneState?.insuredId]);

  //   useEffect(() => {
  //   if (taskPaneState?.insuredId && taskPaneState.comLocId && filterTypeByCriteriaType === "Type Insured/Policy") {
  //     getPoliciesDetailsForInsuredData(taskPaneState?.insuredId, -1, taskPaneState.comLocId, false);
  //   }
  // }, [taskPaneState?.criteria]);

  //   useEffect(() => {
  //   if (!!taskPaneState?.useFileName) {
  //     setTaskPaneState((prev) => ({ ...prev, docDescription: "", sameAsDocDesc: false  }));
  //   }
  // }, [taskPaneState?.useFileName]);

  useEffect(() => {
    if (taskPaneState.userId && taskPaneState?.compLocId) {
      getUserGroupsForPerson(taskPaneState.userId);
    }
  }, [taskPaneState.userId, taskPaneState?.compLocId]);
  useEffect(() => {
    if (taskPaneState.userGroupId) {
      getQueuesForUserGroup(taskPaneState.userGroupId);
      getPersonUserGroupXRef(taskPaneState.userGroupId, true, false);
      getTaskTypes(taskPaneState.userGroupId);
    } else {
      setQueues([]);
      setUsers([]);
      setTaskTypes([]);
    }
  }, [taskPaneState.userGroupId]);

  useEffect(() => {
    if (taskPaneState?.comLocId && taskPaneState?.userId)
      getCheckList(selectedItemDetailFromGrid?.policyID, false, taskPaneState?.comLocId, true);
  }, [taskPaneState?.comLocId, taskPaneState?.userId, selectedItemDetailFromGrid?.policyID]);

  useEffect(() => {}, [selectedItemDetailFromGrid, taskPaneState.comLocId]);

  useEffect(() => {
    if (taskPaneState?.userId) {
      // NOTE: the previous Office.context.ui.messageParent("ready") call was removed --
      // it is invalid in a TASKPANE (messageParent only works in a dialog opened via
      // displayDialogAsync) and threw "Cannot read properties of undefined (reading
      // 'messageParent')" in a plain browser where Office.context is undefined. Its
      // DialogParentMessageReceived handler was already commented out (no consumer), so
      // the "ready" handshake was dead code.
      Office.onReady((info) => {
        const host = info && info.host;
        setOfficeHost(host || null);
        // Outlook WITH a real mail subject (compose / selected mail): run the email-subject
        // parsing path so a policy/submission number in the subject pre-selects the location.
        // setSubject ends up loading locations and sets the shared one-shot ref, so the
        // generic bootstrap below defers to it. A plain taskpane open (empty subject) is
        // left to the generic bootstrap -- the bare setSubject("") path was not reliably
        // loading locations in hosted Outlook (App Insights: GetLocation never fired).
        if (host === Office.HostType.Outlook) {
          const item = sessionStorage.getItem("mailsubject");
          if (item) {
            setSubject(item);
          }
        }
      });
    }
  }, [taskPaneState?.userId]);

  // UNIFIED, HOST-AGNOSTIC location bootstrap. Fires getLocationData() exactly once per session
  // when the user is authenticated (userId from the profile fetch + username) and locations are
  // still empty -- REGARDLESS of Office host. This covers BOTH a plain browser (no Office host)
  // AND a hosted Outlook taskpane opened without a parseable mail subject (where the old
  // setSubject path did not load locations -> empty dropdown -> SearchByCriteria 400).
  // The shared one-shot ref (also set by setSubject / getLocationSpecificInfoEmail) ensures the
  // email-subject path and this generic path never both fire getLocationData.
  useEffect(() => {
    // Defer to the Outlook mail-subject path when a parseable subject is present: setSubject
    // (fired from the async Office.onReady callback) will load locations + set the shared ref.
    // Without this, the generic effect could win the race and double-fire getLocationData on a
    // hosted Outlook compose/selected-mail open. A plain open (no subject) falls through here.
    // A browser NEVER has a mailsubject, so this only defers in (hosted) Outlook. While the
    // host is still unresolved (undefined), defer too if a subject exists so we don't race
    // ahead of the Office.onReady callback that will run setSubject.
    const hasMailSubject = !!sessionStorage.getItem("mailsubject");
    const deferToEmailPath =
      hasMailSubject && (officeHost === undefined || officeHost === Office.HostType.Outlook);
    if (
      taskPaneState?.userId && // profile loaded -> userId headers will be populated
      username &&
      !locationBootstrapped.current &&
      !deferToEmailPath &&
      locationData.length === 0
    ) {
      locationBootstrapped.current = true;
      trackEvent("bootstrap.locations", {
        trigger: "authenticated",
        host: officeHost ? String(officeHost) : "browser",
      });
      getLocationData();
    }
  }, [taskPaneState?.userId, username, locationData.length, officeHost]);

  function containsLetterAndNumber(str) {
    const hasLetter = /[a-zA-Z]/.test(str);
    const hasNumber = /\d/.test(str);
    const hasOnlyAlphanumeric = /^[a-zA-Z0-9]+$/.test(str);
    return (hasLetter && hasNumber && hasOnlyAlphanumeric) || (hasNumber && hasOnlyAlphanumeric);
  }

  function isValidPolicyNumber(value) {
    if (!value) return false;
    value = value.trim();
    // Allowed characters and structure (segments separated by . / -), optional trailing separator
    console.log("value", value);
    const allowedShape = /^[A-Za-z0-9]+(?:[./-][A-Za-z0-9]+)*[./-]?$/i.test(value);
    console.log("allowedShape", allowedShape);

    // Require at least one digit OR one of the separators
    const hasDigitOrSeparator = /[0-9./-]/.test(value);
    console.log("hasDigitOrSeparator", hasDigitOrSeparator);

    return allowedShape && hasDigitOrSeparator;
  }
  const re = /(?:Policy|Pol)(?:\s*(?:Number|Num))?\s*(?:[:#]\s*)*(?:\bRenewal\b\s+of\s+)?([^\s,;]+)/i;

  function extractPolicyNumber(text) {
    const m = text.match(re);
    return m ? m[1].trim() : null;
  }

  const setSubject = (subject) => {
    const matchSubmissionNumber2 = subject.match(/ref\s*[^A-Za-z0-9]*\s*([A-Za-z0-9]+)/i);
    const matchSubmissionNumber = matchSubmissionNumber2 || null;
    const matchPolicyNumber1 = policyNumberCheck(subject);
    console.log("matchPolicyNumber1", matchPolicyNumber1);
    const matchPolicyNumber = matchPolicyNumber1 || null;
    let submissionNumber = null;
    let policyNumber = null;
    if (matchPolicyNumber) {
      policyNumber = matchPolicyNumber ? matchPolicyNumber : null;
    }
    if (matchSubmissionNumber) {
      submissionNumber = containsLetterAndNumber(matchSubmissionNumber[1])
        ? matchSubmissionNumber[1]
        : matchSubmissionNumber[1];
    }
    // Claim the shared one-shot guard: this email-subject path is about to load locations
    // (via getLocationSpecificInfoEmail or the bare getLocationData), so the generic
    // authenticated bootstrap must NOT also fire getLocationData -> exactly one load.
    locationBootstrapped.current = true;
    if (policyNumber || submissionNumber) {
      getLocationSpecificInfoEmail(policyNumber, submissionNumber);
    } else getLocationData();
    setTaskPaneState((prev) => ({ ...prev, emailSubject: subject }));
  };

  const getLocationSpecificInfoEmail = async (policyNumber, submissionNumber) => {
    const operation = requests.start("emailLocation");
    try {
      setCriteriaLoading(true);
      try {
        const result = await services.getLocationSpecificInfo(
          null,
          null,
          taskPaneState?.userId,
          -1,
          null,
          false,
          false,
          true,
          policyNumber,
          submissionNumber,
        );
        if (!requests.current(operation)) return;
        // notify("success", "Location Info data fetch successful!");
        const locationDetailByEmailResults = result?.locationDetailByEmailResults;
        if (
          locationDetailByEmailResults?.compLocId &&
          locationDetailByEmailResults?.companyID &&
          locationDetailByEmailResults?.insuredID &&
          locationDetailByEmailResults?.policyId
        ) {
          setTaskPaneState((prev) => ({
            ...prev,
            ...result,
            useFileName: result?.chkTask ?? false,
            // taskPriority: "01",
            comLocId: locationDetailByEmailResults?.compLocId?.toString() || null,
            compLocId: locationDetailByEmailResults?.compLocId?.toString() || null,
            compId: locationDetailByEmailResults?.companyID || null,
            insuredId: locationDetailByEmailResults?.insuredID || null,
            criteria: locationDetailByEmailResults?.criteria || result?.criteriaItems[0]?.criteriaName,
            isEmailSub: true,
            policyId: locationDetailByEmailResults?.policyId,
          }));
          getLocationData(false);
        } else {
          getLocationData();
        }
        // setDocumentType(result?.documentTypesResults || []);
      } catch (e) {
        if (!requests.current(operation)) return;
        getLocationData();
        if (e?.status === 403) {
          setTitle("Search result");
          let errorText = "Permission does not exist for Company Location tied to selected Policy.";
          if (policyNumber && submissionNumber) {
            errorText = `Permission does not exist for Company Location tied to selected Policy # ${policyNumber} and Submission # ${submissionNumber}`;
          } else if (policyNumber) {
            errorText = `Permission does not exist for Company Location tied to selected Policy # ${policyNumber}`;
          } else if (submissionNumber) {
            errorText = `Permission does not exist for Company Location tied to selected Submission # ${submissionNumber}`;
          }
          setText(errorText);
          setOpen(true);
        } else if (e?.status === 500) {
          notify("error", e?.response?.data?.message || "Failed to fetch Location Info data!");
        } else {
          notify("error", "Failed to fetch Location Info data");
        }
        console.log(e);
        appInsights.trackDependencyData({
          target: process.env.BASE_URL,
          name: "Outlook_LocationSpecificInfo_Payload",
          dependencyTypeName: "HTTPS",
          success: false,
          responseCode: e?.status || 500,
          properties: {
            bodyData: {
              policyNumber,
              submissionNumber,
              userId: taskPaneState?.userId,
            },
            error: e,
            userId: taskPaneState.userId,
          },
        });
      } finally {
        if (requests.current(operation)) setCriteriaLoading(false);
      }
    } finally {
      requests.finish(operation);
    }
  };

  const getLocationData = async (isCheck = true) => {
    const operation = requests.start("locations");
    try {
      setLocationError(false);
      setLocationsLoading(true);
      try {
        notify("progress");
        track("bootstrap.getLocationData.called", { host: officeHost ? String(officeHost) : "browser" });
        const result = await services.bindLocation(username);
        if (!requests.current(operation)) return;
        notify("success", "Location data fetch successful!");
        // KEY diagnostic seam: did locations load, and how many rows. Empty here -> empty
        // Criteria dropdown -> SearchByCriteria 400. This was invisible to AI before.
        trackEvent("bootstrap.getLocationData.result", { rowCount: result?.length ?? 0 });
        setLocationData(result);
        if (result?.length === 1 && isCheck) {
          setPoliciesDetailsForInsured([]);
          setSelectedGridItem([]);
          setCheckList([]);
          const value = result[0]?.compLocID;
          const compId = profileDetails.filter((pd) => pd.compLocID == value)[0]?.companyID;
          setTaskPaneState((prev) => ({
            ...prev,
            comLocId: value,
            compLocId: value,
            compId,
            checklistItem: "",
          }));
          if (value && compId) await getLocationSpecificInfo(value, compId);
        }
      } catch (e) {
        if (!requests.current(operation)) return;
        setLocationError(true);
        notify("error", "Failed to fetch location data");
        console.log(e);
        trackError(e, { seam: "bootstrap.getLocationData" });
      } finally {
        if (requests.current(operation)) setLocationsLoading(false);
      }
    } finally {
      requests.finish(operation);
    }
  };

  const getLocationSpecificInfo = async (comLocId, compId, criteria = "") => {
    const operation = requests.start("location", [taskPaneState.userId, compId, comLocId]);
    setSelectedGridItem([]);
    setPoliciesDetailsForInsured([]);
    setTaskPaneState((prev) => ({ ...prev, documentClass: "", checklistItem: "" }));
    try {
      setCriteriaLoading(true);
      try {
        const userId = taskPaneState?.userId;
        // notify("progress");
        setTaskPaneState((prev) => ({
          ...prev,
          criteria: "",
          insuredId: "",
        }));
        const result = await services.getLocationSpecificInfo(
          comLocId,
          compId,
          userId,
          -1,
          comLocId,
          false,
          false,
        );
        if (!requests.current(operation)) return;
        notify("success", "Location Info data fetch successful!");
        // Criteria cascade seam: how many criteria options the selected location yielded.
        trackEvent("bootstrap.getCriteriaList.result", { criteriaCount: result?.criteriaItems?.length ?? 0 });
        setTaskPaneState((prev) => ({
          ...prev,
          ...result,
          criteria: criteria ? criteria : (result?.criteriaItems[0]?.criteriaName ?? ""),
          insuredId: result?.insuredForRecentPoliciesResults[0]?.insuredID ?? "",
          useFileName: result?.chkTask ?? false,
        }));
        // setDocumentType(result?.documentTypesResults || []);
      } catch (e) {
        if (!requests.current(operation)) return;
        notify("error", "Failed to fetch Location Info data");
        console.log(e);
        trackError(e, { seam: "bootstrap.getLocationSpecificInfo" });
        appInsights.trackDependencyData({
          target: process.env.BASE_URL,
          name: "Outlook_LocationSpecificInfo_Payload",
          dependencyTypeName: "HTTPS",
          success: false,
          responseCode: e?.status || 500,
          properties: {
            bodyData: {
              comLocId,
              compId,
              userId: taskPaneState?.userId,
            },
            error: e,
            userId: taskPaneState.userId,
          },
        });
      } finally {
        if (requests.current(operation)) setCriteriaLoading(false);
      }
    } finally {
      requests.finish(operation);
    }
  };

  return (
    <TaskPaneContext.Provider
      value={{
        profileDetails,
        setProfileDetails,
        locationData,
        setLocationData,
        taskPaneState,
        setTaskPaneState,
        policiesDetailsForInsured,
        setPoliciesDetailsForInsured,
        getPoliciesDetailsForInsuredData,
        filterTypeByCriteriaType,
        selectedGridItem,
        setSelectedGridItem,
        selectedItemDetailFromGrid,
        documentType,
        setDocumentType,
        setDocumentClass,
        documentClass,
        userGroups,
        setUserGroups,
        queues,
        setQueues,
        user,
        setUsers,
        taskTypes,
        setTaskTypes,
        checkList,
        accessToken,
        priorityList,
        setCheckList,
        getLocationSpecificInfo,
        setUsername,
        username,
        metadataLoading,
        loading,
        setLoading,
        locationError,
        retryLocations,
        locationsLoading,
        criteriaLoading,
        formListsLoading,
      }}
    >
      {Toaster}
      {open && (
        <AlertPopup
          title={title}
          text={text}
          open={open}
          handleClose={() => {
            setOpen(false);
            setTitle("");
            setText("");
          }}
        />
      )}
      {children}
    </TaskPaneContext.Provider>
  );
};

export const useTaskPane = () => {
  const taskPaneContext = useContext(TaskPaneContext);

  if (!taskPaneContext) {
    throw new Error("hook canot be used outside of the TaskPaneProvider");
  }
  return taskPaneContext;
};
